import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageSeo } from "@/hooks/usePageSeo";

const faqs = [
  {
    q: "吃瓜现场需要下载App或安装工具吗？",
    a: "完全不需要。吃瓜现场是网页版，浏览器打开即可浏览，免下载、免安装，点开即看，省心又省空间。",
  },
  {
    q: "手机和电脑都能直接打开吗？支持哪些设备？",
    a: "都可以。我们做了响应式适配，手机版与电脑版会自动切换，无论手机、平板还是电脑，使用主流浏览器直接打开就能流畅浏览。",
  },
  {
    q: "内容更新快吗？是不是实时刷新？",
    a: "是的。热点内容会持续滚动更新，娱乐八卦、综艺现场、社会热点与网络热梗都在同步跟进，让你实时跟上热点节奏。",
  },
  {
    q: "浏览需要注册登录吗？",
    a: "不用。直接打开就能阅读全部内容，无需注册、无需登录，真正做到打开即用、零门槛围观。",
  },
  {
    q: "内容太多看不过来，怎么快速找到感兴趣的？",
    a: "你可以通过顶部导航进入对应频道，或使用首页的搜索框、实时热点榜快速定位。话题也整理了分类标签，按兴趣挑着看即可。",
  },
  {
    q: "如何投稿或反馈内容建议？",
    a: "欢迎通过「关于我们」页面提供的邮箱与我们交流，把你的线索、选题与建议发给我们，我们会认真对待每一条反馈。",
  },
];

export default function Faq() {
  const [openIdx, setOpenIdx] = useState(0);

  usePageSeo({
    title: "常见问题 - 吃瓜现场 | 免下载在线看使用指南",
    description:
      "关于吃瓜现场网页在线看、免下载直接打开、手机版电脑版自适应、实时刷新等常见问题解答。无需注册即可浏览，随时随地轻松吃瓜。",
    ld: {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  });

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
      <nav className="flex items-center gap-1.5 text-sm text-foreground-600" aria-label="面包屑">
        <Link to="/" className="hover:text-primary-700">首页</Link>
        <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
        <span className="text-foreground-950">常见问题</span>
      </nav>

      <header className="mt-6 text-center">
        <p className="text-xs font-semibold uppercase tracking-widest text-primary-500">FAQ · 使用指南</p>
        <h1 className="mt-2 font-heading text-3xl font-black tracking-tight text-foreground-950 md:text-4xl">
          常见问题
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-foreground-700">
          关于免下载在线看、直接打开、手机电脑切换与实时浏览的疑问，这里大多都有答案。没找到的，也欢迎联系我们。
        </p>
      </header>

      <div className="mt-8 space-y-3">
        {faqs.map((f, i) => {
          const open = openIdx === i;
          return (
            <div
              key={f.q}
              className={`overflow-hidden rounded-2xl border transition-colors ${
                open ? "border-primary-300 bg-background-50" : "border-background-200/70 bg-background-100/40"
              }`}
            >
              <button
                type="button"
                onClick={() => setOpenIdx(open ? -1 : i)}
                aria-expanded={open}
                className="flex w-full items-center gap-3 px-5 py-4 text-left"
              >
                <span
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full font-heading text-sm font-bold ${
                    open ? "bg-primary-500 text-background-50" : "bg-background-200 text-foreground-700"
                  }`}
                >
                  {i + 1}
                </span>
                <span className="flex-1 font-heading text-base font-bold text-foreground-950">{f.q}</span>
                <i
                  className={`ri-arrow-down-s-line text-xl text-foreground-600 transition-transform ${open ? "rotate-180" : ""}`}
                  aria-hidden="true"
                ></i>
              </button>
              {open && (
                <div className="px-5 pb-5 pl-[52px]">
                  <p className="text-sm leading-relaxed text-foreground-700">{f.a}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-8 rounded-3xl bg-foreground-950 p-6 text-center md:p-8">
        <h2 className="font-heading text-xl font-black text-background-50">还有其他问题？</h2>
        <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-foreground-200">
          想了解更多，可以去「关于我们」页面向我们留言，或先逛逛热点榜和各个频道，体验免下载直接打开浏览的快感。
        </p>
        <div className="mt-5 flex flex-wrap justify-center gap-2.5">
          <Link to="/about" className="inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">
            <i className="ri-customer-service-2-line text-base" aria-hidden="true"></i> 联系我们
          </Link>
          <Link to="/hot" className="inline-flex items-center gap-1.5 rounded-full bg-background-50/15 px-5 py-2.5 text-sm text-background-50 hover:bg-background-50/25">
            <i className="ri-fire-line text-base" aria-hidden="true"></i> 去看看热点
          </Link>
        </div>
      </div>
    </div>
  );
}